import styled from "@emotion/styled";

import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { type CommercialMeterKey } from "@/modules/commercial-plans";
import { getCommercialUsageLimitCopy } from "./copy";

interface CommercialUsageLimitModalProps {
  accepted?: boolean;
  isOpen: boolean;
  meterKey: CommercialMeterKey;
  onClose: () => void;
  onReviewPlans: () => void;
  onReviewExtraUsage?: () => void;
}

/**
 * Shared recovery surface for an authoritative server-side commercial limit
 * decision. Callers should open it only after the backend rejects or pauses an
 * action; the client does not estimate billable capacity locally.
 */
export const CommercialUsageLimitModal = ({
  accepted = false,
  isOpen,
  meterKey,
  onClose,
  onReviewPlans,
  onReviewExtraUsage,
}: CommercialUsageLimitModalProps) => {
  const copy = getCommercialUsageLimitCopy(meterKey, { accepted });

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        {copy.title}
      </MDSText>
      <LimitDialogContent slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphS}>{copy.body}</MDSText>
        <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
          {copy.recovery}
        </MDSText>
        <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
          Extra usage is $50 per block. Mem never adds a paid block without your authorization.
        </MDSText>
      </LimitDialogContent>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton onClick={onClose}>Not now</MDSButton>
        {onReviewExtraUsage ? (
          <MDSButton onClick={onReviewExtraUsage}>Review extra usage</MDSButton>
        ) : null}
        <MDSButton variant={MDSButtonVariant.Emphasis} onClick={onReviewPlans}>
          Compare plans
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
};

const LimitDialogContent = styled(MDSDialogTextContent)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;
